import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './FundingSheet.scss';

const OperationsFundingSheet = ({ user, isPublic }) => {
  const componentRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Check if it's a public view with token
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const validToken = 'siki-investor-2024'; // Change this to your secure token
  
  // Redirect if not authorized
  useEffect(() => {
    if (!isPublic && user?.email !== 'chris@siki.io') {
      window.location.href = '/admin';
    } else if (isPublic && token !== validToken) {
      window.location.href = '/';
    }
  }, [user, isPublic, token]);

  // State for operations budget data only
  const [budgetData, setBudgetData] = useState({
    totalBudget: 2500000, // $2.5M for operations including leadership
    year1: {
      leadership: {
        ceo: { monthly: 15000, months: 12 },
        coo: { monthly: 10000, months: 12 },
        cfo: { monthly: 10000, months: 12 },
        cmo: { monthly: 10000, months: 12 },
        cao: { monthly: 10000, months: 12 },
        businessDev: { monthly: 8333, months: 12 },
        copyrightCoordinator: { monthly: 10000, months: 12 },
        projectManager: { monthly: 8000, months: 12 },
        hrDirector: { monthly: 7000, months: 12 }
      },
      infrastructure: {
        awsHosting: { q1: 2500, q2: 5000, q3: 12000, q4: 20000 },
        liveVideoInfra: { q1: 3000, q2: 7000, q3: 20000, q4: 40000 },
        redisCache: { q1: 800, q2: 1500, q3: 3000, q4: 5000 },
        mongoDatabase: { q1: 1200, q2: 2000, q3: 5000, q4: 8000 },
        cdnCloudfront: { q1: 1000, q2: 2000, q3: 4000, q4: 7000 },
        monitoring: { q1: 500, q2: 750, q3: 1000, q4: 1500 }
      },
      development: {
        backendDev: { monthly: 10000, months: 12, count: 1 },
        mobileDev: { monthly: 10000, months: 12 },
        frontendDev: { monthly: 10000, months: 12 },
        devOps: { monthly: 8000, months: 12 },
        qaDev: { monthly: 7500, months: 12, count: 2 },
        mlEngineer: { monthly: 20000, months: 12 },
        tester: { monthly: 3000, months: 12, count: 4 },
        dataEngineer: { monthly: 15000, months: 12 },
        securityEngineer: { monthly: 15000, months: 12 },
        platformEngineer: { monthly: 15000, months: 12 },
        blockchainEngineer: { monthly: 18000, months: 12 }
      },
      operations: {
        supportTeam: { monthly: 3333, count: 2, months: 12 },
        legal: { monthly: 10000, months: 12 },
        accounting: { monthly: 2000, months: 12 }
      },
      customerSupport: {
        trustSafety: { monthly: 5000, count: 1, months: 12 },
        techSupport: { monthly: 4000, count: 2, months: 12 }
      },
      compliance: {
        generalLiability: { annual: 12000, count: 1 },
        cyberInsurance: { annual: 15000, count: 1 },
        musicLicensing: { annual: 25000, count: 1 },
        dataPrivacy: { quarterly: 10000, quarters: 4 }
      },
      maintenance: {
        securityUpdates: { monthly: 500, months: 12 },
        systemMaintenance: { monthly: 1500, months: 12 },
        backupSolutions: { monthly: 1000, months: 12 },
        monitoring: { monthly: 1000, months: 12 }
      }
    }
  });

  // Calculate totals - same format as original
  const calculateCategoryTotal = (category) => {
    let total = 0;
    Object.keys(category).forEach(key => {
      const item = category[key];
      if (item.monthly && item.months) {
        total += item.monthly * item.months * (item.count || 1);
      } else if (item.q1 !== undefined || item.q2 !== undefined || item.q3 !== undefined || item.q4 !== undefined) {
        total += (item.q1 || 0) + (item.q2 || 0) + (item.q3 || 0) + (item.q4 || 0);
      } else if (item.quarterly && item.quarters) {
        total += item.quarterly * item.quarters;
      } else if (item.annual && item.count) {
        total += item.annual * item.count;
      }
    });
    return total;
  };

  const totals = {
    leadership: calculateCategoryTotal(budgetData.year1.leadership),
    infrastructure: calculateCategoryTotal(budgetData.year1.infrastructure),
    development: calculateCategoryTotal(budgetData.year1.development),
    operations: calculateCategoryTotal(budgetData.year1.operations),
    customerSupport: calculateCategoryTotal(budgetData.year1.customerSupport),
    compliance: calculateCategoryTotal(budgetData.year1.compliance),
    maintenance: calculateCategoryTotal(budgetData.year1.maintenance)
  };

  const grandTotal = Object.values(totals).reduce((acc, val) => acc + val, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (amount, total) => {
    return `${((amount / total) * 100).toFixed(1)}%`;
  };

  // Calculate quarterly operational costs
  const calculateQuarterlyCosts = () => {
    const monthlyFixed = 
      (budgetData.year1.leadership.ceo.monthly || 0) +
      (budgetData.year1.leadership.coo.monthly || 0) +
      (budgetData.year1.leadership.cfo.monthly || 0) +
      (budgetData.year1.leadership.cmo.monthly || 0) +
      (budgetData.year1.leadership.cao.monthly || 0) +
      (budgetData.year1.leadership.businessDev.monthly || 0) +
      (budgetData.year1.leadership.copyrightCoordinator.monthly || 0) +
      (budgetData.year1.leadership.projectManager.monthly || 0) +
      (budgetData.year1.leadership.hrDirector.monthly || 0) +
      ((budgetData.year1.development.backendDev.monthly || 0) * (budgetData.year1.development.backendDev.count || 1)) +
      (budgetData.year1.development.mobileDev.monthly || 0) +
      (budgetData.year1.development.frontendDev.monthly || 0) +
      (budgetData.year1.development.devOps.monthly || 0) +
      ((budgetData.year1.development.qaDev.monthly || 0) * (budgetData.year1.development.qaDev.count || 1)) +
      (budgetData.year1.development.mlEngineer.monthly || 0) +
      ((budgetData.year1.development.tester.monthly || 0) * (budgetData.year1.development.tester.count || 1)) +
      (budgetData.year1.development.dataEngineer.monthly || 0) +
      (budgetData.year1.development.securityEngineer.monthly || 0) +
      (budgetData.year1.development.platformEngineer.monthly || 0) +
      (budgetData.year1.development.blockchainEngineer.monthly || 0) +
      ((budgetData.year1.operations.supportTeam.monthly || 0) * (budgetData.year1.operations.supportTeam.count || 1)) +
      (budgetData.year1.operations.legal.monthly || 0) +
      (budgetData.year1.operations.accounting.monthly || 0) +
      ((budgetData.year1.customerSupport.trustSafety.monthly || 0) * (budgetData.year1.customerSupport.trustSafety.count || 1)) +
      ((budgetData.year1.customerSupport.techSupport.monthly || 0) * (budgetData.year1.customerSupport.techSupport.count || 1)) +
      (budgetData.year1.maintenance.securityUpdates.monthly || 0) +
      (budgetData.year1.maintenance.systemMaintenance.monthly || 0) +
      (budgetData.year1.maintenance.backupSolutions.monthly || 0) +
      (budgetData.year1.maintenance.monitoring.monthly || 0);

    const q1Costs = (monthlyFixed * 3) +
      ((budgetData.year1.infrastructure.awsHosting.q1 + 
        budgetData.year1.infrastructure.liveVideoInfra.q1 + 
        budgetData.year1.infrastructure.redisCache.q1 + 
        budgetData.year1.infrastructure.mongoDatabase.q1 + 
        budgetData.year1.infrastructure.cdnCloudfront.q1 + 
        budgetData.year1.infrastructure.monitoring.q1) * 3) +
      (budgetData.year1.compliance.dataPrivacy.quarterly) +
      ((budgetData.year1.compliance.generalLiability.annual + 
        budgetData.year1.compliance.cyberInsurance.annual + 
        budgetData.year1.compliance.musicLicensing.annual) / 4);

    const q2Costs = (monthlyFixed * 3) +
      ((budgetData.year1.infrastructure.awsHosting.q2 + 
        budgetData.year1.infrastructure.liveVideoInfra.q2 + 
        budgetData.year1.infrastructure.redisCache.q2 + 
        budgetData.year1.infrastructure.mongoDatabase.q2 + 
        budgetData.year1.infrastructure.cdnCloudfront.q2 + 
        budgetData.year1.infrastructure.monitoring.q2) * 3) +
      (budgetData.year1.compliance.dataPrivacy.quarterly) +
      ((budgetData.year1.compliance.generalLiability.annual + 
        budgetData.year1.compliance.cyberInsurance.annual + 
        budgetData.year1.compliance.musicLicensing.annual) / 4);

    const q3Costs = (monthlyFixed * 3) +
      ((budgetData.year1.infrastructure.awsHosting.q3 + 
        budgetData.year1.infrastructure.liveVideoInfra.q3 + 
        budgetData.year1.infrastructure.redisCache.q3 + 
        budgetData.year1.infrastructure.mongoDatabase.q3 + 
        budgetData.year1.infrastructure.cdnCloudfront.q3 + 
        budgetData.year1.infrastructure.monitoring.q3) * 3) +
      (budgetData.year1.compliance.dataPrivacy.quarterly) +
      ((budgetData.year1.compliance.generalLiability.annual + 
        budgetData.year1.compliance.cyberInsurance.annual + 
        budgetData.year1.compliance.musicLicensing.annual) / 4);

    const q4Costs = (monthlyFixed * 3) +
      ((budgetData.year1.infrastructure.awsHosting.q4 + 
        budgetData.year1.infrastructure.liveVideoInfra.q4 + 
        budgetData.year1.infrastructure.redisCache.q4 + 
        budgetData.year1.infrastructure.mongoDatabase.q4 + 
        budgetData.year1.infrastructure.cdnCloudfront.q4 + 
        budgetData.year1.infrastructure.monitoring.q4) * 3) +
      (budgetData.year1.compliance.dataPrivacy.quarterly) +
      ((budgetData.year1.compliance.generalLiability.annual + 
        budgetData.year1.compliance.cyberInsurance.annual + 
        budgetData.year1.compliance.musicLicensing.annual) / 4);

    return { q1: q1Costs, q2: q2Costs, q3: q3Costs, q4: q4Costs };
  };

  const quarterlyCosts = calculateQuarterlyCosts();

  // PDF Generation Function
  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    const element = componentRef.current;
    
    try {
      // Hide the download button during capture
      const downloadBtn = element.querySelector('.download-pdf-btn');
      if (downloadBtn) downloadBtn.style.display = 'none';
      
      // Capture the component as canvas
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#1a1a1a' // Match the dark theme
      });
      
      // Show the button again
      if (downloadBtn) downloadBtn.style.display = 'block';
      
      // Calculate dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let position = 0;
      
      // Add the image to PDF
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add new pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Save the PDF
      pdf.save('Siki-Operations-Funding-Sheet.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Allow public view with valid token
  if (!isPublic && user?.email !== 'chris@siki.io') {
    return null;
  }

  return (
    <div className="fundingSheet" ref={componentRef}>
      <div className="fundingHeader" style={{ position: 'relative' }}>
        <h1>Operations Funding Sheet - Leadership & Core Operations</h1>
        <button 
          className="download-pdf-btn"
          onClick={generatePDF}
          disabled={isGeneratingPDF}
          style={{
            position: 'absolute',
            right: '20px',
            top: '20px',
            padding: '10px 20px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: isGeneratingPDF ? 'wait' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            opacity: isGeneratingPDF ? 0.7 : 1
          }}
        >
          {isGeneratingPDF ? 'Generating PDF...' : 'Download as PDF'}
        </button>
        <div className="budgetSummary">
          <div className="summaryCard">
            <span className="label">Total Operations Budget</span>
            <span className="value">{formatCurrency(budgetData.totalBudget)}</span>
          </div>
          <div className="summaryCard">
            <span className="label">Year 1 Operations</span>
            <span className="value">{formatCurrency(grandTotal)}</span>
          </div>
          <div className="summaryCard">
            <span className="label">Monthly Burn Rate</span>
            <span className="value">{formatCurrency(grandTotal / 12)}</span>
          </div>
        </div>
      </div>

      {/* Quarterly Operations Summary */}
      <div className="quarterlyOverview">
        <h2>Quarterly Operations Summary</h2>
        <div className="quarterlyGrid">
          <div className="quarterCard">
            <span className="quarterLabel">Q1</span>
            <span className="quarterValue">{formatCurrency(quarterlyCosts.q1)}</span>
          </div>
          <div className="quarterCard">
            <span className="quarterLabel">Q2</span>
            <span className="quarterValue">{formatCurrency(quarterlyCosts.q2)}</span>
          </div>
          <div className="quarterCard">
            <span className="quarterLabel">Q3</span>
            <span className="quarterValue">{formatCurrency(quarterlyCosts.q3)}</span>
          </div>
          <div className="quarterCard">
            <span className="quarterLabel">Q4</span>
            <span className="quarterValue">{formatCurrency(quarterlyCosts.q4)}</span>
          </div>
        </div>
      </div>

      <div className="fundingTables">
        {/* Infrastructure */}
        <div className="categorySection">
          <h2>Infrastructure ({formatPercent(totals.infrastructure, grandTotal)}) - Scaling from 50K to 2M Users</h2>
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Q1 (50K)</th>
                <th>Q2 (250K)</th>
                <th>Q3 (1M)</th>
                <th>Q4 (2M)</th>
                <th>Year 1 Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>AWS EC2/ECS</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.awsHosting.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.awsHosting.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.awsHosting.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.awsHosting.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q1 + budgetData.year1.infrastructure.awsHosting.q2 + budgetData.year1.infrastructure.awsHosting.q3 + budgetData.year1.infrastructure.awsHosting.q4) * 3)}</td>
              </tr>
              <tr>
                <td>Live Video Infrastructure</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.liveVideoInfra.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.liveVideoInfra.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.liveVideoInfra.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.liveVideoInfra.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.liveVideoInfra.q1 + budgetData.year1.infrastructure.liveVideoInfra.q2 + budgetData.year1.infrastructure.liveVideoInfra.q3 + budgetData.year1.infrastructure.liveVideoInfra.q4) * 3)}</td>
              </tr>
              <tr>
                <td>Redis Cache</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.redisCache.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.redisCache.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.redisCache.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.redisCache.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.redisCache.q1 + budgetData.year1.infrastructure.redisCache.q2 + budgetData.year1.infrastructure.redisCache.q3 + budgetData.year1.infrastructure.redisCache.q4) * 3)}</td>
              </tr>
              <tr>
                <td>MongoDB Atlas</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.mongoDatabase.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.mongoDatabase.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.mongoDatabase.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.mongoDatabase.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.mongoDatabase.q1 + budgetData.year1.infrastructure.mongoDatabase.q2 + budgetData.year1.infrastructure.mongoDatabase.q3 + budgetData.year1.infrastructure.mongoDatabase.q4) * 3)}</td>
              </tr>
              <tr>
                <td>CloudFront CDN</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.cdnCloudfront.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.cdnCloudfront.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.cdnCloudfront.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.cdnCloudfront.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.cdnCloudfront.q1 + budgetData.year1.infrastructure.cdnCloudfront.q2 + budgetData.year1.infrastructure.cdnCloudfront.q3 + budgetData.year1.infrastructure.cdnCloudfront.q4) * 3)}</td>
              </tr>
              <tr>
                <td>Monitoring/Logging</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.monitoring.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.monitoring.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.monitoring.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.monitoring.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.monitoring.q1 + budgetData.year1.infrastructure.monitoring.q2 + budgetData.year1.infrastructure.monitoring.q3 + budgetData.year1.infrastructure.monitoring.q4) * 3)}</td>
              </tr>
              <tr style={{backgroundColor: '#3a3a3a', fontWeight: 'bold'}}>
                <td>Quarterly Total</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q1 + budgetData.year1.infrastructure.liveVideoInfra.q1 + budgetData.year1.infrastructure.redisCache.q1 + budgetData.year1.infrastructure.mongoDatabase.q1 + budgetData.year1.infrastructure.cdnCloudfront.q1 + budgetData.year1.infrastructure.monitoring.q1) * 3)}</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q2 + budgetData.year1.infrastructure.liveVideoInfra.q2 + budgetData.year1.infrastructure.redisCache.q2 + budgetData.year1.infrastructure.mongoDatabase.q2 + budgetData.year1.infrastructure.cdnCloudfront.q2 + budgetData.year1.infrastructure.monitoring.q2) * 3)}</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q3 + budgetData.year1.infrastructure.liveVideoInfra.q3 + budgetData.year1.infrastructure.redisCache.q3 + budgetData.year1.infrastructure.mongoDatabase.q3 + budgetData.year1.infrastructure.cdnCloudfront.q3 + budgetData.year1.infrastructure.monitoring.q3) * 3)}</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q4 + budgetData.year1.infrastructure.liveVideoInfra.q4 + budgetData.year1.infrastructure.redisCache.q4 + budgetData.year1.infrastructure.mongoDatabase.q4 + budgetData.year1.infrastructure.cdnCloudfront.q4 + budgetData.year1.infrastructure.monitoring.q4) * 3)}</td>
                <td style={{color: '#ffee20'}}>{formatCurrency(totals.infrastructure)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Leadership Team */}
        <div className="categorySection">
          <h2>Leadership Team ({formatPercent(totals.leadership, grandTotal)})</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Monthly</th>
                <th>Annual</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>CEO</td>
                <td>{formatCurrency(budgetData.year1.leadership.ceo.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.ceo.monthly * budgetData.year1.leadership.ceo.months)}</td>
              </tr>
              <tr>
                <td>COO</td>
                <td>{formatCurrency(budgetData.year1.leadership.coo.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.coo.monthly * budgetData.year1.leadership.coo.months)}</td>
              </tr>
              <tr>
                <td>CFO</td>
                <td>{formatCurrency(budgetData.year1.leadership.cfo.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.cfo.monthly * budgetData.year1.leadership.cfo.months)}</td>
              </tr>
              <tr>
                <td>CMO</td>
                <td>{formatCurrency(budgetData.year1.leadership.cmo.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.cmo.monthly * budgetData.year1.leadership.cmo.months)}</td>
              </tr>
              <tr>
                <td>CAO (Chief Administrative Officer)</td>
                <td>{formatCurrency(budgetData.year1.leadership.cao.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.cao.monthly * budgetData.year1.leadership.cao.months)}</td>
              </tr>
              <tr>
                <td>Business Development</td>
                <td>{formatCurrency(budgetData.year1.leadership.businessDev.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.businessDev.monthly * budgetData.year1.leadership.businessDev.months)}</td>
              </tr>
              <tr>
                <td>Copyright Coordinator/Specialist</td>
                <td>{formatCurrency(budgetData.year1.leadership.copyrightCoordinator.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.copyrightCoordinator.monthly * budgetData.year1.leadership.copyrightCoordinator.months)}</td>
              </tr>
              <tr>
                <td>Project Manager</td>
                <td>{formatCurrency(budgetData.year1.leadership.projectManager.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.projectManager.monthly * budgetData.year1.leadership.projectManager.months)}</td>
              </tr>
              <tr>
                <td>HR Director</td>
                <td>{formatCurrency(budgetData.year1.leadership.hrDirector.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.leadership.hrDirector.monthly * budgetData.year1.leadership.hrDirector.months)}</td>
              </tr>
              <tr className="totalRow">
                <td>Total</td>
                <td>{formatCurrency(
                  budgetData.year1.leadership.ceo.monthly +
                  budgetData.year1.leadership.coo.monthly +
                  budgetData.year1.leadership.cfo.monthly +
                  budgetData.year1.leadership.cmo.monthly +
                  budgetData.year1.leadership.cao.monthly +
                  budgetData.year1.leadership.businessDev.monthly +
                  budgetData.year1.leadership.copyrightCoordinator.monthly +
                  budgetData.year1.leadership.projectManager.monthly +
                  budgetData.year1.leadership.hrDirector.monthly
                )}</td>
                <td>{formatCurrency(totals.leadership)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Development Team */}
        <div className="categorySection">
          <h2>Development Team ({formatPercent(totals.development, grandTotal)})</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Monthly</th>
                <th>Count</th>
                <th>Annual</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Backend Developer</td>
                <td>{formatCurrency(budgetData.year1.development.backendDev.monthly)}</td>
                <td>{budgetData.year1.development.backendDev.count || 1}</td>
                <td>{formatCurrency(budgetData.year1.development.backendDev.monthly * budgetData.year1.development.backendDev.months * (budgetData.year1.development.backendDev.count || 1))}</td>
              </tr>
              <tr>
                <td>Mobile Developer</td>
                <td>{formatCurrency(budgetData.year1.development.mobileDev.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.development.mobileDev.monthly * budgetData.year1.development.mobileDev.months)}</td>
              </tr>
              <tr>
                <td>Frontend Developer</td>
                <td>{formatCurrency(budgetData.year1.development.frontendDev.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.development.frontendDev.monthly * budgetData.year1.development.frontendDev.months)}</td>
              </tr>
              <tr>
                <td>DevOps Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.devOps.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.development.devOps.monthly * budgetData.year1.development.devOps.months)}</td>
              </tr>
              <tr>
                <td>QA Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.qaDev.monthly)}</td>
                <td>{budgetData.year1.development.qaDev.count || 1}</td>
                <td>{formatCurrency(budgetData.year1.development.qaDev.monthly * budgetData.year1.development.qaDev.months * (budgetData.year1.development.qaDev.count || 1))}</td>
              </tr>
              <tr>
                <td>AI/ML Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.mlEngineer.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.development.mlEngineer.monthly * budgetData.year1.development.mlEngineer.months)}</td>
              </tr>
              <tr>
                <td>Tester</td>
                <td>{formatCurrency(budgetData.year1.development.tester.monthly)}</td>
                <td>{budgetData.year1.development.tester.count || 1}</td>
                <td>{formatCurrency(budgetData.year1.development.tester.monthly * budgetData.year1.development.tester.months * (budgetData.year1.development.tester.count || 1))}</td>
              </tr>
              <tr>
                <td>Data Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.dataEngineer.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.development.dataEngineer.monthly * budgetData.year1.development.dataEngineer.months)}</td>
              </tr>
              <tr>
                <td>Security Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.securityEngineer.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.development.securityEngineer.monthly * budgetData.year1.development.securityEngineer.months)}</td>
              </tr>
              <tr>
                <td>Platform/Infrastructure Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.platformEngineer.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.development.platformEngineer.monthly * budgetData.year1.development.platformEngineer.months)}</td>
              </tr>
              <tr>
                <td>Blockchain Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.blockchainEngineer.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.development.blockchainEngineer.monthly * budgetData.year1.development.blockchainEngineer.months)}</td>
              </tr>
              <tr className="totalRow">
                <td>Total</td>
                <td colSpan="2">{formatCurrency(
                  (budgetData.year1.development.backendDev.monthly * (budgetData.year1.development.backendDev.count || 1)) +
                  budgetData.year1.development.mobileDev.monthly +
                  budgetData.year1.development.frontendDev.monthly +
                  budgetData.year1.development.devOps.monthly +
                  (budgetData.year1.development.qaDev.monthly * (budgetData.year1.development.qaDev.count || 1)) +
                  budgetData.year1.development.mlEngineer.monthly +
                  (budgetData.year1.development.tester.monthly * (budgetData.year1.development.tester.count || 1)) +
                  budgetData.year1.development.dataEngineer.monthly +
                  budgetData.year1.development.securityEngineer.monthly +
                  budgetData.year1.development.platformEngineer.monthly +
                  budgetData.year1.development.blockchainEngineer.monthly
                )}</td>
                <td>{formatCurrency(totals.development)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Operations Team */}
        <div className="categorySection">
          <h2>Operations Team ({formatPercent(totals.operations, grandTotal)})</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Monthly</th>
                <th>Count</th>
                <th>Annual</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Support Team</td>
                <td>{formatCurrency(budgetData.year1.operations.supportTeam.monthly)}</td>
                <td>{budgetData.year1.operations.supportTeam.count}</td>
                <td>{formatCurrency(budgetData.year1.operations.supportTeam.monthly * budgetData.year1.operations.supportTeam.count * budgetData.year1.operations.supportTeam.months)}</td>
              </tr>
              <tr>
                <td>Legal Counsel</td>
                <td>{formatCurrency(budgetData.year1.operations.legal.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.operations.legal.monthly * budgetData.year1.operations.legal.months)}</td>
              </tr>
              <tr>
                <td>Accounting</td>
                <td>{formatCurrency(budgetData.year1.operations.accounting.monthly)}</td>
                <td>1</td>
                <td>{formatCurrency(budgetData.year1.operations.accounting.monthly * budgetData.year1.operations.accounting.months)}</td>
              </tr>
              <tr className="totalRow">
                <td>Total</td>
                <td colSpan="2"></td>
                <td>{formatCurrency(totals.operations)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Customer Support */}
        <div className="categorySection">
          <h2>Customer Support ({formatPercent(totals.customerSupport, grandTotal)})</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Monthly</th>
                <th>Count</th>
                <th>Annual</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Trust & Safety</td>
                <td>{formatCurrency(budgetData.year1.customerSupport.trustSafety.monthly)}</td>
                <td>{budgetData.year1.customerSupport.trustSafety.count}</td>
                <td>{formatCurrency(budgetData.year1.customerSupport.trustSafety.monthly * budgetData.year1.customerSupport.trustSafety.count * budgetData.year1.customerSupport.trustSafety.months)}</td>
              </tr>
              <tr>
                <td>Technical Support</td>
                <td>{formatCurrency(budgetData.year1.customerSupport.techSupport.monthly)}</td>
                <td>{budgetData.year1.customerSupport.techSupport.count}</td>
                <td>{formatCurrency(budgetData.year1.customerSupport.techSupport.monthly * budgetData.year1.customerSupport.techSupport.count * budgetData.year1.customerSupport.techSupport.months)}</td>
              </tr>
              <tr className="totalRow">
                <td>Total</td>
                <td colSpan="2"></td>
                <td>{formatCurrency(totals.customerSupport)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Compliance & Insurance */}
        <div className="categorySection">
          <h2>Compliance & Insurance ({formatPercent(totals.compliance, grandTotal)})</h2>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Frequency</th>
                <th>Amount</th>
                <th>Annual Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>General Liability Insurance</td>
                <td>Annual</td>
                <td>{formatCurrency(budgetData.year1.compliance.generalLiability.annual)}</td>
                <td>{formatCurrency(budgetData.year1.compliance.generalLiability.annual)}</td>
              </tr>
              <tr>
                <td>Cyber Security Insurance</td>
                <td>Annual</td>
                <td>{formatCurrency(budgetData.year1.compliance.cyberInsurance.annual)}</td>
                <td>{formatCurrency(budgetData.year1.compliance.cyberInsurance.annual)}</td>
              </tr>
              <tr>
                <td>Music Licensing (ASCAP/BMI)</td>
                <td>Annual</td>
                <td>{formatCurrency(budgetData.year1.compliance.musicLicensing.annual)}</td>
                <td>{formatCurrency(budgetData.year1.compliance.musicLicensing.annual)}</td>
              </tr>
              <tr>
                <td>Data Privacy Compliance</td>
                <td>Quarterly</td>
                <td>{formatCurrency(budgetData.year1.compliance.dataPrivacy.quarterly)}</td>
                <td>{formatCurrency(budgetData.year1.compliance.dataPrivacy.quarterly * budgetData.year1.compliance.dataPrivacy.quarters)}</td>
              </tr>
              <tr className="totalRow">
                <td>Total</td>
                <td colSpan="2"></td>
                <td>{formatCurrency(totals.compliance)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Maintenance & Security */}
        <div className="categorySection">
          <h2>Maintenance & Security ({formatPercent(totals.maintenance, grandTotal)})</h2>
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Monthly</th>
                <th>Annual</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Security Updates & Patches</td>
                <td>{formatCurrency(budgetData.year1.maintenance.securityUpdates.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.maintenance.securityUpdates.monthly * budgetData.year1.maintenance.securityUpdates.months)}</td>
              </tr>
              <tr>
                <td>System Maintenance</td>
                <td>{formatCurrency(budgetData.year1.maintenance.systemMaintenance.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.maintenance.systemMaintenance.monthly * budgetData.year1.maintenance.systemMaintenance.months)}</td>
              </tr>
              <tr>
                <td>Backup Solutions</td>
                <td>{formatCurrency(budgetData.year1.maintenance.backupSolutions.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.maintenance.backupSolutions.monthly * budgetData.year1.maintenance.backupSolutions.months)}</td>
              </tr>
              <tr>
                <td>Monitoring & Alerts</td>
                <td>{formatCurrency(budgetData.year1.maintenance.monitoring.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.maintenance.monitoring.monthly * budgetData.year1.maintenance.monitoring.months)}</td>
              </tr>
              <tr className="totalRow">
                <td>Total</td>
                <td>{formatCurrency(
                  budgetData.year1.maintenance.securityUpdates.monthly +
                  budgetData.year1.maintenance.systemMaintenance.monthly +
                  budgetData.year1.maintenance.backupSolutions.monthly +
                  budgetData.year1.maintenance.monitoring.monthly
                )}</td>
                <td>{formatCurrency(totals.maintenance)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Grand Total Summary */}
        <div className="grandTotalSection">
          <h2>Year 1 Operations Summary</h2>
          <table className="summaryTable">
            <tbody>
              <tr>
                <td>Leadership Team</td>
                <td>{formatCurrency(totals.leadership)}</td>
                <td>{formatPercent(totals.leadership, grandTotal)}</td>
              </tr>
              <tr>
                <td>Infrastructure</td>
                <td>{formatCurrency(totals.infrastructure)}</td>
                <td>{formatPercent(totals.infrastructure, grandTotal)}</td>
              </tr>
              <tr>
                <td>Development Team</td>
                <td>{formatCurrency(totals.development)}</td>
                <td>{formatPercent(totals.development, grandTotal)}</td>
              </tr>
              <tr>
                <td>Operations Team</td>
                <td>{formatCurrency(totals.operations)}</td>
                <td>{formatPercent(totals.operations, grandTotal)}</td>
              </tr>
              <tr>
                <td>Customer Support</td>
                <td>{formatCurrency(totals.customerSupport)}</td>
                <td>{formatPercent(totals.customerSupport, grandTotal)}</td>
              </tr>
              <tr>
                <td>Compliance & Insurance</td>
                <td>{formatCurrency(totals.compliance)}</td>
                <td>{formatPercent(totals.compliance, grandTotal)}</td>
              </tr>
              <tr>
                <td>Maintenance & Security</td>
                <td>{formatCurrency(totals.maintenance)}</td>
                <td>{formatPercent(totals.maintenance, grandTotal)}</td>
              </tr>
              <tr className="grandTotalRow">
                <td>Total Year 1 Operations</td>
                <td>{formatCurrency(grandTotal)}</td>
                <td>100%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Key Metrics */}
        <div className="keyMetrics">
          <h2>Key Operational Metrics</h2>
          <div className="metricsGrid">
            <div className="metricCard">
              <span className="metricLabel">Monthly Burn Rate</span>
              <span className="metricValue">{formatCurrency(grandTotal / 12)}</span>
            </div>
            <div className="metricCard">
              <span className="metricLabel">Infrastructure %</span>
              <span className="metricValue">{formatPercent(totals.infrastructure, grandTotal)}</span>
            </div>
            <div className="metricCard">
              <span className="metricLabel">Team Costs %</span>
              <span className="metricValue">{formatPercent(totals.development + totals.operations + totals.customerSupport, grandTotal)}</span>
            </div>
            <div className="metricCard">
              <span className="metricLabel">Q4 Monthly Run Rate</span>
              <span className="metricValue">{formatCurrency(quarterlyCosts.q4 / 3)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="notesSection">
          <h3>Operations Budget Notes</h3>
          <ul>
            <li>• Includes C-suite leadership team compensation</li>
            <li>• Infrastructure scales with user growth from 50K to 2M users</li>
            <li>• Development team remains constant throughout Year 1</li>
            <li>• No marketing, sales, or influencer costs included</li>
            <li>• Focus on core operational expenses and leadership</li>
            <li>• Compliance and insurance costs are essential for platform operation</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OperationsFundingSheet;